/**
 * StudioCustomImageGenerator — Freeform image generation from text prompts
 *
 * Allows users to type any prompt and generate a branded image via Gemini AI,
 * independent of the pipeline's suggested visualizations.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Sparkles,
  Wand2,
  Download,
  RefreshCw,
  Loader2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { notify } from '@/lib/notify'

interface Props {
  sessionId: string
  onImageSaved?: () => void
}

/** Convert base64 PNG to an object URL for efficient rendering */
function base64ToBlobUrl(base64: string): string {
  const bytes = atob(base64)
  const buf = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i)
  const blob = new Blob([buf], { type: 'image/png' })
  return URL.createObjectURL(blob)
}

export function StudioCustomImageGenerator({ sessionId, onImageSaved }: Props) {
  const [prompt, setPrompt] = useState('')
  const [generating, setGenerating] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const prevUrlRef = useRef<string | null>(null)

  // Revoke old blob URL when a new one is set
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
    }
  }, [])

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      notify.error('Please enter a prompt')
      return
    }
    setGenerating(true)
    setError(null)
    try {
      const viz = await api.studio.generateCustomImage(sessionId, prompt.trim())
      // Revoke previous blob URL
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
      const url = base64ToBlobUrl(viz.pngBase64 || '')
      prevUrlRef.current = url
      setImageUrl(url)
      notify.success('Image generated and saved')
      onImageSaved?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Generation failed'
      setError(msg)
      notify.error('Image generation failed', { description: msg })
    } finally {
      setGenerating(false)
    }
  }, [sessionId, prompt])

  const handleDownload = useCallback(() => {
    if (!imageUrl) return
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = `custom-image-${Date.now()}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [imageUrl])

  return (
    <div className="border-t border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-surface-900/50">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-white/10">
        <Sparkles className="w-4 h-4 text-purple-500" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Custom Image Generator
        </h3>
        <span className="text-xs text-gray-400">
          Generate any image from a text prompt
        </span>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* Prompt textarea */}
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !generating && prompt.trim()) {
              handleGenerate()
            }
          }}
          rows={3}
          placeholder="Describe the image you want to generate... (Cmd+Enter to generate)"
          className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white resize-y focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
        />

        {/* Generate button */}
        <button
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {generating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Wand2 className="w-3.5 h-3.5" />
          )}
          {generating ? 'Generating...' : 'Generate Image'}
        </button>

        {/* Error display */}
        {error && (
          <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
        )}

        {/* Generated image */}
        {imageUrl && (
          <div className="space-y-2">
            <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 inline-block">
              <img
                src={imageUrl}
                alt="Custom generated image"
                className="max-h-96 w-auto"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10 transition-colors"
              >
                <Download className="w-3 h-3" />
                Download
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10 disabled:opacity-50 transition-colors"
              >
                {generating ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Regenerate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
